/**
 * src/pages/portfolio/defacto/index.tsx
 *
 * File purpose:
 * - Render the DeFacto client detail page.
 * - Show existing "Original Media" items from data (no upload/embed controls in production).
 * - Render Videos section (includes requested YouTube embed) and Instagram embeds.
 */

import React, { useMemo, useState } from 'react'
import { Eye, Trash2, Download, Star, Edit2, Check, X } from 'lucide-react'
import InstagramPair from '../../../components/embeds/InstagramPair'
import VideoEmbed from '../../../components/embeds/VideoEmbed'
import { clientsData } from '../../../data/clientsData'

/**
 * MediaItem
 *
 * Interface:
 * - Represents a media entry stored in originals (image/video/iframe).
 */
interface MediaItem {
  /** Unique id for React rendering and local operations */
  id: string
  /** Source URL for the media */
  src: string
  /** Media type (controls rendering) */
  type: 'image' | 'video' | 'iframe'
  /** Optional human‑readable title */
  title?: string
  /** Whether this item is marked as featured */
  featured?: boolean
}

/**
 * chunkArray
 *
 * Helper:
 * - Split an array into chunks of given size.
 *
 * @param arr source array
 * @param size chunk size
 * @returns array of chunks
 */
function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size))
  return chunks
}

/**
 * MediaCard
 *
 * Component:
 * - Render a single media item in the grid with hover settings (view, feature, edit title, download, remove).
 */
function MediaCard({
  item,
  onRemove,
  onToggleFeatured,
  onUpdateTitle
}: {
  item: MediaItem
  onRemove: (id: string) => void
  onToggleFeatured: (id: string) => void
  onUpdateTitle: (id: string, title: string) => void
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [localTitle, setLocalTitle] = useState(item.title ?? '')

  /**
   * handleSave
   *
   * Save edited title and exit editing mode.
   */
  function handleSave() {
    onUpdateTitle(item.id, localTitle)
    setEditing(false)
  }

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
        {item.type === 'video' ? (
          <video className="w-full h-full object-cover" muted preload="metadata">
            <source src={item.src} />
          </video>
        ) : item.type === 'iframe' ? (
          <div className="w-full h-full flex items-center justify-center text-sm text-gray-700 bg-gray-900/60">
            Embed
          </div>
        ) : (
          <img src={item.src} alt={item.title ?? item.id} className="w-full h-full object-cover" />
        )}
      </div>

      <div className="p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded-md text-sm w-full"
                placeholder="Title"
              />
              <button
                onClick={handleSave}
                className="p-1 rounded-md bg-green-600 text-white"
                title="Save"
                type="button"
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => {
                  setEditing(false)
                  setLocalTitle(item.title ?? '')
                }}
                className="p-1 rounded-md bg-white border border-gray-200"
                title="Cancel"
                type="button"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <>
              <div className="text-sm font-medium text-gray-900 truncate">{item.title ?? 'Untitled'}</div>
              <div className="text-xs text-gray-500">{item.type.toUpperCase()}</div>
            </>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <a
            href={item.src}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-gray-50 text-gray-600"
            title="View"
          >
            <Eye size={16} />
          </a>

          <button
            onClick={() => onToggleFeatured(item.id)}
            className={`p-2 rounded-md ${
              item.featured ? 'bg-yellow-50 text-yellow-600' : 'hover:bg-gray-50 text-gray-600'
            }`}
            title="Feature"
            type="button"
          >
            <Star size={16} />
          </button>

          <button
            onClick={() => setEditing(true)}
            className="p-2 rounded-md hover:bg-gray-50 text-gray-600"
            title="Edit title"
            type="button"
          >
            <Edit2 size={16} />
          </button>

          <a href={item.src} download className="p-2 rounded-md hover:bg-gray-50 text-gray-600" title="Download">
            <Download size={16} />
          </a>

          <button
            onClick={() => onRemove(item.id)}
            className="p-2 rounded-md hover:bg-red-50 text-red-600"
            title="Remove"
            type="button"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {item.featured && (
        <div className="absolute top-3 left-3 rounded-md bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5">
          Featured
        </div>
      )}
    </div>
  )
}

/**
 * DeFactoPage
 *
 * Component:
 * - Render header, originals grid (read-only in production), videos and Instagram pairs.
 */
export default function DeFactoPage(): JSX.Element {
  // Find the DeFacto client entry
  const client = clientsData.find((c) => c.id === 'defacto')

  // Defensive: render fallback when client missing
  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200 text-center">
          <h2 className="text-lg font-semibold">DeFacto not found</h2>
          <p className="text-sm text-gray-600 mt-2">The requested client profile is not available.</p>
        </div>
      </div>
    )
  }

  // Default Instagram pair URLs
  const defaultInstagramUrls = [
    'https://www.instagram.com/reels/C6tDTveM1NN/embed/',
    'https://www.instagram.com/reel/C5LVe0WCrHs/embed/'
  ]

  // Initialize originals from client data (defensive)
  const initialOriginals = Array.isArray(client.mediaLinks?.originals) ? client.mediaLinks!.originals : []

  /**
   * Local state for originals (images / embeds / videos).
   * - In this deployed version we only show what comes from client data.
   */
  const [originals, setOriginals] = useState<MediaItem[]>(
    () =>
      initialOriginals.map((o, idx) => {
        const src = String(o)
        const type =
          (src.startsWith('http') && (src.includes('instagram.com') || src.includes('<iframe'))) ||
          src.includes('instagram.com')
            ? 'iframe'
            : src.startsWith('http') && /\.(mp4|webm|ogg)(\?|$)/i.test(src)
            ? 'video'
            : 'image'
        return { id: `init-${idx}`, src, type, title: undefined, featured: false } as MediaItem
      })
  )

  /**
   * removeItem
   *
   * - Remove a media item from originals (local-only adjustment).
   *
   * @param id MediaItem id
   */
  function removeItem(id: string) {
    setOriginals((s) => s.filter((m) => m.id !== id))
  }

  /**
   * toggleFeatured
   *
   * - Toggle featured status for a media item.
   *
   * @param id MediaItem id
   */
  function toggleFeatured(id: string) {
    setOriginals((s) => s.map((m) => (m.id === id ? { ...m, featured: !m.featured } : m)))
  }

  /**
   * updateTitle
   *
   * - Update the title for a media item.
   *
   * @param id MediaItem id
   * @param title new title
   */
  function updateTitle(id: string, title: string) {
    setOriginals((s) => s.map((m) => (m.id === id ? { ...m, title } : m)))
  }

  /**
   * instagramUrls
   *
   * - Keep default pair + any instagram iframe URLs present in originals.
   * - Deduplicate and keep order: default pair first.
   */
  const instagramUrls = useMemo(() => {
    const fromOriginals = originals
      .filter((o) => o.type === 'iframe' && o.src.includes('instagram.com'))
      .map((o) => o.src)
    const all = [...defaultInstagramUrls, ...fromOriginals]
    return Array.from(new Set(all))
  }, [originals])

  // chunk instagramUrls into pairs so each InstagramPair renders two at most
  const instagramChunks = useMemo(() => chunkArray(instagramUrls, 2), [instagramUrls])

  // Prepare first pair and force one of the slots to a specific embed (keeps previous logic)
  const firstPairSrcs = instagramChunks.length ? [...instagramChunks[0]].reverse() : [...defaultInstagramUrls].reverse()
  // Replace the second slot with the requested DeFacto reel (embed URL)
  firstPairSrcs[1] = 'https://www.instagram.com/reel/C6tDTveM1NN/embed'

  /**
   * videos
   *
   * - Combine any client-provided videos with the requested YouTube embed.
   * - Deduplicate to avoid duplicates.
   */
  const videos = useMemo(() => {
    const fromClient = Array.isArray(client.mediaLinks?.videos) ? client.mediaLinks!.videos : []
    const requested = 'https://www.youtube.com/embed/qcwdNohA-q4?si=JaC0hL_QKGS9xXFQ'
    return Array.from(new Set([...fromClient, requested]))
  }, [client.mediaLinks])

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 flex items-center gap-6">
          <img
            src={client.logo}
            alt={`${client.name} logo`}
            className="h-16 w-16 object-contain rounded-md border border-gray-200 bg-white p-1"
            onError={(e) => {
              e.currentTarget.src =
                'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
            }}
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {typeof client.description === 'string' ? client.description : ''}
            </p>
          </div>
        </header>

        {/* Main layout: two columns on large screens */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <main className="lg:col-span-2 space-y-6">
            {/* Original Media (read-only grid) */}
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Original Media</h2>
              </div>

              {/* Grid of originals; if empty, nothing is rendered (no placeholder text) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {originals.map((it) => (
                  <MediaCard
                    key={it.id}
                    item={it}
                    onRemove={removeItem}
                    onToggleFeatured={toggleFeatured}
                    onUpdateTitle={updateTitle}
                  />
                ))}
              </div>
            </section>

            {/* Videos section */}
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Videos</h3>
                <p className="text-sm text-gray-600 mt-1">YouTube embeds and hosted videos related to DeFacto.</p>
              </div>

              {videos.length === 0 ? (
                <div className="text-sm text-gray-500 p-6">No videos available for DeFacto.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videos.map((v, i) => (
                    <div key={`vid-${i}`} className="rounded-lg overflow-hidden border border-gray-200">
                      <VideoEmbed src={v} />
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Instagram (iframe pairs) */}
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <InstagramPair srcs={firstPairSrcs} titles={['DeFacto post 2', 'DeFacto post 1']} size={350} title="Instagram" />
              <div className="mt-6 flex flex-col items-center gap-6">
                {instagramChunks.slice(1).map((chunk, i) => (
                  <InstagramPair key={`ig-chunk-${i + 1}`} srcs={chunk} size={350} />
                ))}
              </div>
            </section>
          </main>

          {/* Right sidebar */}
          <aside className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Client details</h3>
              <dl className="text-sm text-gray-600 space-y-2">
                <div>
                  <dt className="font-medium text-gray-800">Media Planning</dt>
                </div>
                <div>
                  <dt className="font-medium text-gray-800">Influencer Marketing</dt>
                </div>
                <div>
                  <dt className="font-medium text-gray-800">
                    <a
                      href="https://www.instagram.com/defacto.global/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      Instagram
                    </a>
                  </dt>
                </div>
              </dl>
            </div>

            {/* Quick actions - only Back to Portfolio */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-600">
              <div className="font-medium text-gray-800 mb-2">Quick actions</div>
              <div>
                <a
                  href="#/portfolio"
                  className="inline-flex w-full items-center justify-center h-9 px-3 rounded-md bg-primary text-white text-sm"
                >
                  Back to Portfolio
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}